/**
 * Coups de pouce — Attendu 647 « Connaître l'unité centimètre cube ».
 *
 * Une entrée par `generator_key` (cf. gen_647.js). Aide de VOCABULAIRE
 * uniquement (idée utilisateur, 2026-07-31) : montrer ce qu'est une arête —
 * le mot-obstacle du gen — sans jamais énoncer la définition du cm³ (qui EST
 * la réponse des QCM/trous). Le relier (unités ↔ grandeurs) n'a pas d'entrée :
 * l'aide y serait hors sujet.
 */

// ── Cube ANIMÉ : les 12 arêtes s'allument une par une (idée utilisateur) ──
// Animation CSS embarquée — la sheet injecte le SVG inline, donc ça joue
// nativement. prefers-reduced-motion : cube statique, arête gauche orange.
// Couleurs EXPLICITES (jamais de currentColor — thème sombre élève).
// Arêtes visibles sombres, cachées en pointillés gris, l'arête exemple en
// orange épais avec son étiquette.
const SVG_CUBE_ARETE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 150" width="210" height="131" role="img"
     aria-label="Un cube en perspective : ses douze arêtes s'allument une par une">
  <style>
    .ov { opacity: 0; }
    @media (prefers-reduced-motion: no-preference) {
      .ov { animation: cp647pop 6s linear infinite; }
    .o1 { animation-delay: 0.0s; }
    .o2 { animation-delay: 0.5s; }
    .o3 { animation-delay: 1.0s; }
    .o4 { animation-delay: 1.5s; }
    .o5 { animation-delay: 2.0s; }
    .o6 { animation-delay: 2.5s; }
    .o7 { animation-delay: 3.0s; }
    .o8 { animation-delay: 3.5s; }
    .o9 { animation-delay: 4.0s; }
    .o10 { animation-delay: 4.5s; }
    .o11 { animation-delay: 5.0s; }
    .o12 { animation-delay: 5.5s; }
    }
    @keyframes cp647pop { 0%, 100% { opacity: 0; } 3%, 8% { opacity: 1; } 12% { opacity: 0; } }
  </style>
  <line x1='70' y1='125' x2='70' y2='45' stroke='#0f172a' stroke-width='2'/>
  <line x1='70' y1='45' x2='150' y2='45' stroke='#0f172a' stroke-width='2'/>
  <line x1='150' y1='45' x2='150' y2='125' stroke='#0f172a' stroke-width='2'/>
  <line x1='150' y1='125' x2='70' y2='125' stroke='#0f172a' stroke-width='2'/>
  <line x1='70' y1='45' x2='100' y2='22' stroke='#0f172a' stroke-width='2'/>
  <line x1='150' y1='45' x2='180' y2='22' stroke='#0f172a' stroke-width='2'/>
  <line x1='150' y1='125' x2='180' y2='102' stroke='#0f172a' stroke-width='2'/>
  <line x1='70' y1='125' x2='100' y2='102' stroke='#94a3b8' stroke-width='1.5' stroke-dasharray='4,3'/>
  <line x1='100' y1='22' x2='180' y2='22' stroke='#0f172a' stroke-width='2'/>
  <line x1='180' y1='22' x2='180' y2='102' stroke='#0f172a' stroke-width='2'/>
  <line x1='180' y1='102' x2='100' y2='102' stroke='#94a3b8' stroke-width='1.5' stroke-dasharray='4,3'/>
  <line x1='100' y1='102' x2='100' y2='22' stroke='#94a3b8' stroke-width='1.5' stroke-dasharray='4,3'/>
  <line x1='70' y1='45' x2='70' y2='125' stroke='#fb923c' stroke-width='4'/>
  <line class='ov o1' x1='70' y1='125' x2='70' y2='45' stroke='#fb923c' stroke-width='4'/>
  <line class='ov o2' x1='70' y1='45' x2='150' y2='45' stroke='#fb923c' stroke-width='4'/>
  <line class='ov o3' x1='150' y1='45' x2='150' y2='125' stroke='#fb923c' stroke-width='4'/>
  <line class='ov o4' x1='150' y1='125' x2='70' y2='125' stroke='#fb923c' stroke-width='4'/>
  <line class='ov o5' x1='70' y1='45' x2='100' y2='22' stroke='#fb923c' stroke-width='4'/>
  <line class='ov o6' x1='150' y1='45' x2='180' y2='22' stroke='#fb923c' stroke-width='4'/>
  <line class='ov o7' x1='150' y1='125' x2='180' y2='102' stroke='#fb923c' stroke-width='4'/>
  <line class='ov o8' x1='70' y1='125' x2='100' y2='102' stroke='#fb923c' stroke-width='4'/>
  <line class='ov o9' x1='100' y1='22' x2='180' y2='22' stroke='#fb923c' stroke-width='4'/>
  <line class='ov o10' x1='180' y1='22' x2='180' y2='102' stroke='#fb923c' stroke-width='4'/>
  <line class='ov o11' x1='180' y1='102' x2='100' y2='102' stroke='#fb923c' stroke-width='4'/>
  <line class='ov o12' x1='100' y1='102' x2='100' y2='22' stroke='#fb923c' stroke-width='4'/>
  <text x="62" y="88" text-anchor="end" font-size="13" font-weight="bold" fill="#fb923c">une arête</text>
</svg>`.trim();

const FICHE_ARETE = {
    pages: [
        {
            // Ligne 1 (revue 2026-07-31) : l'ancrage VOLUME — l'image fondatrice
            // du chapitre (dénombrement d'unités cubiques) — sans énoncer la
            // définition du cm³ (qui reste la réponse à trouver).
            titre: 'Volume et arête',
            lignes: [
                'Le **volume**, c\'est la place qu\'occupe un objet. On le mesure en comptant les **petits cubes** qu\'il faut pour le remplir.',
                'Une **arête**, c\'est un segment qui borde un solide : un cube en a **12**, toutes de la **même longueur**.',
                'Quand on donne « l\'arête d\'un cube », on donne la longueur d\'**un seul** de ces segments.',
            ],
            svg: SVG_CUBE_ARETE,
        },
    ],
};

const CP_647 = {
    definition_cm3_qcm_unique: FICHE_ARETE,
    definition_cm3_texte_trou_saisie: FICHE_ARETE,
};

export default CP_647;
